/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  batch,
  isAbortError,
  onConnect,
  onDisconnect,
  reaction,
  sleep,
  withAssign,
  type CtxSpy,
} from '@reatom/framework'
import { nanoid } from 'nanoid'
import { match } from 'ts-pattern'

import { AsyncStateN, Duration, Result } from '@blade/std-lib'

import { reatomAsyncState } from './reatom-async-state'

import type { Equivalence } from '@blade/std-lib'
import type { AbortError, Ctx, ReactionAtom } from '@reatom/framework'
import type { Error } from '@root/shared/core'

export interface ResourceRetryOptions<E> {
  enabled?: boolean
  maxAttempts?: number
  delay?: Duration.Duration
  strategy?: 'fixed' | 'linear' | 'exponential'
  retryOn?: (error: E) => boolean
}
export interface ReatomResourceOptions<E, A> {
  name?: string
  /**
   * @default true
   */
  loadOnConnect?: boolean
  /**
   * @default false
   */
  resetOnDisconnect?: boolean
  /**
   * Reconnect means atom was once connected, disconnected and then connected again.
   * @default 'none'
   */
  reconnectStrategy?: 'none' | 'refresh' | 'reload'
  retry?: ResourceRetryOptions<E>
  eq?: Equivalence.Equivalence<A>
}

type RunReason = 'default' | 'refresh' | 'retry'
type RunParams = { reason: RunReason }

export const SET_NOT_INITIALIZED = Symbol('reatom-resource/SET_NOT_INITIALIZED')
export const KEEP_LOADING = Symbol('reatom-resource/KEEP_LOADING')

export const reatomResource = <E, A>(
  fn: (
    ctx: CtxSpy,
    { reason }: { reason: RunReason },
  ) => Promise<
    Result.Result<A, E> | typeof KEEP_LOADING | typeof SET_NOT_INITIALIZED
  >,
  options?: ReatomResourceOptions<E, A>,
) => {
  const resourceName = options?.name || `resource_${nanoid()}`
  const stateAtom = reatomAsyncState(
    AsyncStateN.notInitiated<E | Error.UnknownError, A>(),
    {
      name: resourceName,
      eq: options?.eq,
    },
  )
  const isRefreshingAtom = atom(false, `${resourceName}.isRefreshingAtom`)
  const isRetryingAtom = atom(false, `${resourceName}.isRetryingAtom`)

  const retryOptions = {
    maxAttempts: 4,
    strategy: 'linear',
    delay: Duration.seconds(1),
    ...options?.retry,
  } satisfies ResourceRetryOptions<E>

  const {
    name = 'unknown',
    loadOnConnect = true,
    resetOnDisconnect = false,
    reconnectStrategy = 'none',
  } = options ?? {}

  const runParamsAtom = atom<RunParams>({
    reason: 'default',
  })

  let currentReaction: ReactionAtom<unknown> | undefined

  const callFn = async (ctx: CtxSpy, runParams: RunParams) => {
    try {
      return await fn(ctx, { reason: runParams.reason })
    } catch (error) {
      if (!isAbortError(error)) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
      return Result.fail(error as E)
    }
  }

  const handleRetriesAction = action(
    async (
      ctx: Ctx,
      {
        error,
        runParams,
      }: {
        error: E
        runParams: RunParams
      },
    ) => {
      const currentState_ = ctx.get(stateAtom)
      let attempt = 0
      let shouldRetry_ = shouldRetry(error, attempt)

      while (shouldRetry_) {
        const delay = computeDelayMs(attempt, retryOptions)
        await ctx.schedule(() => sleep(Duration.toMillis(delay)))

        batch(ctx, () => {
          if (runParams.reason !== 'refresh') {
            stateAtom.setLoading(ctx)
          }

          isRetryingAtom(ctx, true)
        })

        const ctx_: CtxSpy = {
          ...ctx,
          spy: ctx.get,
        }

        // https://t.me/reatom_ru/100819
        // eslint-disable-next-line @reatom/async-rule
        const retryResult = await callFn(ctx_, { reason: 'retry' })

        batch(ctx, () => {
          handleResultAction(ctx, {
            result: retryResult,
            currentState: currentState_,
            runParams,
          })

          if (
            retryResult === KEEP_LOADING ||
            retryResult === SET_NOT_INITIALIZED
          ) {
            shouldRetry_ = false
            return
          }

          if (Result.isSuccess(retryResult)) {
            shouldRetry_ = false
            return
          }

          const error = retryResult.failure
          if (isAbortError(error)) {
            shouldRetry_ = false
            return
          }
          attempt++
          shouldRetry_ = shouldRetry(retryResult.failure, attempt)
        })
      }
    },
    `reatom-resource.${name}.handleRetriesAction`,
  )

  const handleResultAction = action(
    (
      ctx: Ctx,
      {
        result,
        currentState,
        runParams,
      }: {
        result:
          | Result.Result<A, E | AbortError>
          | typeof KEEP_LOADING
          | typeof SET_NOT_INITIALIZED
        currentState: AsyncStateN.AsyncStateN<E | Error.UnknownError, A>
        runParams: RunParams
      },
    ) => {
      if (result === KEEP_LOADING) {
        return
      }

      isRefreshingAtom(ctx, false)
      isRetryingAtom(ctx, false)

      if (result === SET_NOT_INITIALIZED) {
        stateAtom.setNotInitiated(ctx)
        return
      }

      if (Result.isSuccess(result)) {
        stateAtom.setSuccess(ctx, result.success)
        return
      }

      if (runParams.reason === 'refresh') {
        return
      }

      if (isAbortError(result.failure)) {
        stateAtom.set(ctx, currentState)
        return
      }

      stateAtom.setError(ctx, result.failure)
    },
    `reatom-resource.${name}.handleResultAction`,
  )

  const runReaction = reaction(async (ctx) => {
    const currentState = ctx.get(stateAtom)

    const runParams = ctx.get(runParamsAtom)
    runParamsAtom(ctx, { reason: 'default' })

    if (runParams.reason === 'refresh') {
      isRefreshingAtom(ctx, true)
    } else {
      stateAtom.setLoading(ctx)
    }

    // https://t.me/reatom_ru/100819
    // eslint-disable-next-line @reatom/async-rule
    const result = await callFn(ctx, runParams)

    handleResultAction(ctx, { result, currentState, runParams })
    if (typeof result === 'object' && Result.isFailure(result)) {
      // Awaited (not fire-and-forget) so the retry loop is part of the reaction
      // body: when the resource is unsubscribed or re-run mid-retry, reatom's
      // concurrent wrapper aborts and `.catch(noop)`s the body promise, instead
      // of the abort leaking as an unhandled rejection.
      // eslint-disable-next-line @reatom/async-rule
      await handleRetriesAction(ctx, { error: result.failure, runParams })
    }
  }, `reatom-resource.${name}.runReaction`)

  const shouldRetry = (error: E, attempt: number) => {
    if (!retryOptions.enabled) {
      return false
    }
    if (retryOptions.retryOn && !retryOptions.retryOn(error)) {
      return false
    }

    return attempt < retryOptions.maxAttempts
  }

  const runAction = action((ctx) => {
    currentReaction?.unsubscribe()
    currentReaction = runReaction(ctx)
  }, `reatom-resource.${name}.runAction`)

  const refreshAction = action((ctx) => {
    runParamsAtom(ctx, { reason: 'refresh' })
    return runAction(ctx)
  }, `reatom-resource.${name}.refreshAction`)

  onConnect(stateAtom, (ctx) => {
    const state = ctx.get(stateAtom)
    if (AsyncStateN.isLoading(state)) {
      return
    }

    if (AsyncStateN.isSuccess(state)) {
      if (reconnectStrategy === 'refresh') {
        refreshAction(ctx)
        return
      }

      if (reconnectStrategy === 'reload') {
        runAction(ctx)
        return
      }
      return
    }

    if (loadOnConnect) {
      runAction(ctx)
    }
  })

  onDisconnect(stateAtom, (ctx) => {
    currentReaction?.unsubscribe()
    if (resetOnDisconnect) {
      stateAtom.set(ctx, AsyncStateN.notInitiated())
    }
  })

  return stateAtom.pipe(
    withAssign(() => ({
      run: runAction,
      refresh: refreshAction,
      isRefreshing: isRefreshingAtom,
    })),
  )
}

export type ResourceAtom<E, A> = ReturnType<typeof reatomResource<E, A>>

const computeDelayMs = (
  /** 0-based */
  attempt: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: ResourceRetryOptions<any>,
): Duration.Duration => {
  const { delay = Duration.seconds(1), strategy = 'linear' } = options

  return match(strategy)
    .with('fixed', () => delay)
    .with('linear', () => Duration.times(delay, attempt + 1))
    .with('exponential', () => Duration.times(delay, 2 ** attempt))
    .exhaustive()
}
